import { ScrollView, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { SafeAreaView } from 'react-native-safe-area-context';
import { ArrowLeft, Archive, BookOpenCheck, ClipboardList, Target } from 'lucide-react-native';
import { ExamReportSummary } from '../components/business/ExamReportSummary';
import { LossReasonCard } from '../components/business/LossReasonCard';
import { AppButton } from '../components/ui/AppButton';
import { AppCard } from '../components/ui/AppCard';
import { AppText } from '../components/ui/AppText';
import { tokens } from '../theme/tokens';

const metrics = [
  { label: '班级排名', value: '12/48', helper: '较上次提升 5 名' },
  { label: '失分题', value: '6', helper: '集中在函数与几何' },
  { label: '预计提升', value: '12分', helper: '完成攻坚任务后' },
];

const abilities = [
  { label: '计算准确', value: '86%', helper: '稳定' },
  { label: '审题理解', value: '72%', helper: '需加强' },
  { label: '步骤表达', value: '68%', helper: '重点训练' },
];

const lossReasons = ['审题时忽略隐藏条件', '函数图像和几何关系迁移不够熟', '解答题步骤表达少了关键推导'];
const weakPoints = ['二次函数顶点式', '相似三角形', '动点轨迹'];

export default function ExamDetailPage() {
  const router = useRouter();

  return (
    <SafeAreaView style={styles.screen}>
      <View style={styles.header}>
        <AppButton
          label="报告"
          variant="ghost"
          onPress={() => router.back()}
          leftSlot={<ArrowLeft size={18} color={tokens.colors.primary} />}
          style={styles.backButton}
        />
        <View style={styles.headerCopy}>
          <AppText variant="title" weight="bold">
            考试分析报告
          </AppText>
          <AppText variant="caption" tone="muted">
            ExamDetailScreen 单场考试归档详情
          </AppText>
        </View>
      </View>

      <ScrollView contentContainerStyle={styles.content}>
        <AppCard style={styles.archiveCard}>
          <Archive size={24} color={tokens.colors.primary} />
          <View style={styles.archiveCopy}>
            <AppText variant="subtitle" weight="bold">
              已归档：九年级数学周测
            </AppText>
            <AppText variant="body" tone="muted">
              归档后可从我的页面或试卷分析入口继续查看本次报告。
            </AppText>
          </View>
        </AppCard>

        <ExamReportSummary score="82" metrics={metrics} abilities={abilities} />
        <LossReasonCard reasons={lossReasons} weakPoints={weakPoints} />

        <AppCard style={styles.sectionCard}>
          <View style={styles.sectionHeader}>
            <Target size={20} color={tokens.colors.warning} />
            <AppText variant="subtitle" weight="bold">
              薄弱知识点攻坚
            </AppText>
          </View>
          {weakPoints.map((point, index) => (
            <View key={point} style={styles.taskRow}>
              <View style={styles.taskIndex}>
                <AppText variant="caption" tone="primary" weight="bold">
                  {index + 1}
                </AppText>
              </View>
              <View style={styles.taskCopy}>
                <AppText variant="body" weight="bold">
                  {point}
                </AppText>
                <AppText variant="caption" tone="muted">
                  推荐 15 分钟专项练习，完成后回看错因。
                </AppText>
              </View>
            </View>
          ))}
        </AppCard>

        <AppCard style={styles.sectionCard}>
          <View style={styles.sectionHeader}>
            <ClipboardList size={20} color={tokens.colors.primary} />
            <AppText variant="subtitle" weight="bold">
              后续动作
            </AppText>
          </View>
          <View style={styles.actions}>
            <AppButton
              label="去再练一次"
              onPress={() => router.push('/wrong-question-practice')}
              leftSlot={<BookOpenCheck size={18} color={tokens.colors.white} />}
            />
            <AppButton
              label="返回试卷分析"
              variant="secondary"
              onPress={() => router.push('/paper-analysis')}
            />
          </View>
        </AppCard>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: tokens.colors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.md,
    paddingHorizontal: tokens.layout.screenPadding,
    paddingTop: tokens.spacing.md,
    paddingBottom: tokens.spacing.sm,
  },
  backButton: {
    minHeight: 40,
    paddingHorizontal: tokens.spacing.sm,
  },
  headerCopy: {
    flex: 1,
  },
  content: {
    paddingHorizontal: tokens.layout.screenPadding,
    paddingTop: tokens.spacing.md,
    paddingBottom: tokens.spacing.xxl,
    gap: tokens.spacing.lg,
  },
  archiveCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.md,
    backgroundColor: tokens.colors.surfaceMuted,
  },
  archiveCopy: {
    flex: 1,
    gap: tokens.spacing.xs,
  },
  sectionCard: {
    gap: tokens.spacing.md,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
  },
  taskRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.md,
    borderRadius: tokens.radius.md,
    padding: tokens.spacing.md,
    backgroundColor: tokens.colors.background,
  },
  taskIndex: {
    width: 28,
    height: 28,
    borderRadius: tokens.radius.full,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colors.surfaceMuted,
  },
  taskCopy: {
    flex: 1,
    gap: tokens.spacing.xs,
  },
  actions: {
    gap: tokens.spacing.md,
  },
});
